import React, { useEffect, useState } from "react";
import { classNames } from "primereact/utils";
import { REIInputNumber, REIDropDown, REILinkButton, REIInputText } from "../../../../components/ui";
import { COUNTRY, LOCATION_ADVANCED, LOCATION_ADVANCED_AU, LOCATION_ADVANCED_NZ } from "../../../../utils/constants";
import LocalityComponent from "./LocalityComponent";
import { RadioButton } from "primereact/radiobutton";
import { AutoComplete } from "primereact/autocomplete";
import { useHttpGets } from "../../../../services/httpService";
import { ApiEndPoints } from "../../../../utils/endpoints";
import { InputNumber } from "primereact/inputnumber";

interface ILocationComponentProps {
  selectedState?: any;
  optionsdata: any;
  selectedDropdownValue: any;
  handleDropdownChange: (e: any, dropdownName: string) => void;
  country: string;
  showMapOptions?: boolean;
  resetFilters?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

interface SuburbSuggestion {
  FilterRegionID: number;
  FilterStateID: number;
  FilterSuburbID: number;
  Label: string;
  Suburb: string;
}

const LocationComponent: React.FC<ILocationComponentProps> = ({
  selectedState,
  optionsdata,
  selectedDropdownValue,
  handleDropdownChange,
  country,
  showMapOptions = true,
  resetFilters = false,
  onValidationChange,
}) => {
  const [postcode, setPostCode] = useState<number | null>(null);
  const [street, setStreet] = useState<string>("");
  const [suburb, setSuburb] = useState<string>("");
  const [limitSuburb, setLimitSuburb] = useState<SuburbSuggestion | null>();
  const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbSuggestion[]>([]);
  const [items, setItems] = useState<SuburbSuggestion[]>([]);
  const [advancedLocation, setAdvancedLocation] = useState<number>();
  const [showMoreLocation, setShowMoreLocation] = useState<boolean>(false);
  const [suburbClassification, setSuburbClassification] = useState("");

  const toggleMoreLocation = () => {
    setShowMoreLocation(!showMoreLocation);
  };

  useEffect(() => {
    if (resetFilters) {
      setAdvancedLocation(undefined);
      setPostCode(null);
      setStreet("");
      setSuburb("");
      setLimitSuburb(null);
      setSuburbSuggestions([]);
      setItems([]);
      setShowMoreLocation(false);
      setSuburbClassification("");
      if (selectedDropdownValue) {
        selectedDropdownValue.locationAdanced = {
          limitPostcode: null,
          ofPostcode: null,
          postcodeRadiusFrom: null,
          postcodeRadiusTo: null,
          selectedSuburbType: null,
        };
      }
    }
  }, [resetFilters]);

  const setClassifications = (e) => {
    setSuburbClassification(e.value);
    selectedDropdownValue.locationAdanced.selectedSuburbType = e.value;
    if (selectedDropdownValue?.locationAdanced) {
      selectedDropdownValue.locationAdanced = {
        ...selectedDropdownValue.locationAdanced,
        selectedSuburbType: e.value?.toString() ?? "",
      };
    } else {
      console.warn("locationAdanced is undefined");
    }
  };

  useEffect(() => {
    if (!limitSuburb) return;
    useHttpGets(
      `${ApiEndPoints.parametersuburb}/${country}/KeyValues/AutoSuggest?countryCode=${country}&prefix=${limitSuburb?.Label}`
    )
      .then((result) => {
        if (result.status === 200) {
          const data = result.data as unknown as SuburbSuggestion[];
          if (data) {
            setItems((data as any).AutoSuburbs);
          }
        }
      })
      .catch((error) => console.log(error));
  }, [limitSuburb]);

  const searchItems = (event: { query: string }) => {
    setTimeout(() => {
      setSuburbSuggestions(items);
    }, 250);
  };

  useEffect(() => {
    if (!selectedDropdownValue?.locationSearchType) return;
    switch (selectedDropdownValue?.locationSearchType) {
      case LOCATION_ADVANCED.POSTCODE_LIMIT:
        setAdvancedLocation(LOCATION_ADVANCED.POSTCODE_LIMIT);
        setPostCode(selectedDropdownValue?.locationAdanced?.limitPostcode);
        break;
      case LOCATION_ADVANCED.POSTCODE_RADIUS:
        setAdvancedLocation(LOCATION_ADVANCED.POSTCODE_RADIUS);
        selectedDropdownValue.postcodeRadiusFrom = parseInt(selectedDropdownValue?.locationAdanced?.postcodeRadiusFrom);
        selectedDropdownValue.postcodeRadiusTo = parseInt(selectedDropdownValue?.locationAdanced?.postcodeRadiusTo);
        setPostCode(selectedDropdownValue?.locationAdanced?.ofPostcode);
        break;
      case LOCATION_ADVANCED.SUBURB_TYPE:
        setAdvancedLocation(LOCATION_ADVANCED.SUBURB_TYPE);
        setSuburbClassification(selectedDropdownValue?.locationAdanced?.selectedSuburbType);
        break;
      default:
        const locationSearchType = selectedDropdownValue?.locationSearchType;
        const locationAdanced = selectedDropdownValue?.locationAdanced;
        if (
          locationSearchType === LOCATION_ADVANCED.ADDRESS_LIMIT ||
          (locationAdanced.limitStreet !== undefined && locationAdanced?.limitStreet !== "")
        ) {
          setAdvancedLocation(LOCATION_ADVANCED.ADDRESS_LIMIT);
          setStreet(locationAdanced?.limitStreet);
        } else if (
          locationSearchType === LOCATION_ADVANCED.SUBURB_LIMIT ||
          (locationAdanced?.limitSuburb !== "" && country === COUNTRY.AU)
        ) {
          setAdvancedLocation(LOCATION_ADVANCED.SUBURB_LIMIT);
          if (!locationAdanced?.limitSuburb || locationAdanced?.limitSuburb == "false") {
            setSuburb("");
            setLimitSuburb({
              FilterRegionID: locationAdanced?.FilterRegionID,
              FilterStateID: locationAdanced?.FilterStateID,
              FilterSuburbID: locationAdanced?.FilterSuburbID,
              Label: "",
              Suburb: locationAdanced?.limitSuburb,
            } as SuburbSuggestion);
          } else {
            setSuburb(locationAdanced?.limitSuburb);
            setLimitSuburb({
              FilterRegionID: locationAdanced?.FilterRegionID,
              FilterStateID: locationAdanced?.FilterStateID,
              FilterSuburbID: locationAdanced?.FilterSuburbID,
              Label: locationAdanced?.limitSuburb,
              Suburb: locationAdanced?.limitSuburb,
            } as SuburbSuggestion);
          }
        } else if (
          locationSearchType === LOCATION_ADVANCED.SUBURB_LIMIT ||
          (locationAdanced?.suburbRadiusFrom !== "" &&
            locationAdanced?.suburbRadiusTo !== 0 &&
            locationAdanced?.OflimitSuburb != null &&
            country !== COUNTRY.AU)
        ) {
          setAdvancedLocation(LOCATION_ADVANCED.SUBURB_LIMIT);
          selectedDropdownValue.suburbRadiusFrom = parseInt(locationAdanced?.suburbRadiusFrom);
          selectedDropdownValue.suburbRadiusTo = parseInt(locationAdanced?.suburbRadiusTo);
          setSuburb(locationAdanced?.OflimitSuburb);
        }
        break;
    }
  }, [selectedDropdownValue?.locationSearchType, selectedDropdownValue?.locationAdanced]);

  return (
    <>
      <LocalityComponent
        selectedState={selectedState}
        optionsdata={optionsdata}
        country={country}
        selectedDropdownValue={selectedDropdownValue}
        resetFilters={resetFilters}
        onValidationChange={onValidationChange}
      />
      {showMoreLocation && (
        <>
          <div className="flex flex-col xl:flex-row lg:w-full gap-1">
            <div className="flex-0 xl:w-1/2 w-full">
              <label className="block text-sm font-medium">Location Advanced</label>
              <REIDropDown
                id="loc-drop-advanced"
                value={advancedLocation}
                onSelectionChange={(e) => {
                  setAdvancedLocation(e.value);
                  selectedDropdownValue.locationSearchType = e.value;
                }}
                data={country === COUNTRY.AU ? LOCATION_ADVANCED_AU : LOCATION_ADVANCED_NZ}
                label="name"
                optionValue="code"
                placeholder="Limit postcode"
              />
            </div>
            {(advancedLocation == 0 || advancedLocation == LOCATION_ADVANCED.POSTCODE_LIMIT) && (
              <div className="flex-0 xl:w-1/2 w-full">
                <label className="block text-sm font-medium">
                  Postcode
                  {country == COUNTRY.AU && (
                    <REILinkButton
                      id="loc-lnk-postcodesearch"
                      data-testid="loc-lnk-postcodesearch"
                      href="http://www1.auspost.com.au/postcodes/"
                      className="no-underline"
                      target="_blank"
                      rel=""
                    >
                      <span className="ml-1">(Postcode Search)</span>
                    </REILinkButton>
                  )}
                </label>
                <REIInputNumber
                  id="loc-num-postcode"
                  value={postcode ?? undefined}
                  onValueChange={(e) => {
                    setPostCode(e.value !== undefined ? e.value : null);
                    if (selectedDropdownValue?.locationAdanced) {
                      selectedDropdownValue.locationAdanced = {
                        ...selectedDropdownValue.locationAdanced,
                        limitPostcode: e.value?.toString() ?? "",
                      };
                    } else {
                      console.warn("locationAdanced is undefined");
                    }
                  }}
                  onBlur={(e) => {
                    setPostCode(e.target.value !== undefined ? e.target.value : null);
                    if (selectedDropdownValue?.locationAdanced) {
                      selectedDropdownValue.locationAdanced.limitPostcode = e.target.value?.toString() ?? "";
                    } else {
                      console.warn("locationAdanced is undefined");
                    }
                  }}
                  useGrouping={false}
                  className="p-inputtext-sm w-full"
                />
              </div>
            )}
            {advancedLocation != null &&
              advancedLocation > 0 &&
              advancedLocation === LOCATION_ADVANCED.ADDRESS_LIMIT && (
                <div className="flex-0 xl:w-1/2 w-full">
                  <label className="block text-sm font-medium">Street Name</label>
                  <REIInputText
                    id="loc-txt-streetname"
                    type="text"
                    value={street}
                    name="streetname"
                    onChange={(e) => {
                      setStreet(e.target.value);
                      if (selectedDropdownValue?.locationAdanced) {
                        selectedDropdownValue.locationAdanced = {
                          ...selectedDropdownValue.locationAdanced,
                          limitStreet: e.target.value,
                        };
                      }
                    }}
                    className="w-full p-inputtext-sm"
                  />
                </div>
              )}
            {advancedLocation != null &&
              advancedLocation > 0 &&
              advancedLocation === LOCATION_ADVANCED.SUBURB_LIMIT &&
              country === COUNTRY.AU && (
                <div className="flex-0 lg:w-1/2 w-full">
                  <label className="block text-sm font-medium">Suburb</label>
                  <AutoComplete
                    inputId="loc-txt-limitsuburb"
                    data-testid="loc-txt-limitsuburb"
                    value={limitSuburb}
                    field="Label"
                    suggestions={suburbSuggestions}
                    completeMethod={searchItems}
                    placeholder=""
                    className="w-full"
                    inputClassName="w-full p-inputtext-sm"
                    minLength={2}
                    delay={300}
                    onChange={(e) => {
                      if (!e.value || e.value === "") {
                        setLimitSuburb(null);
                        if (selectedDropdownValue?.locationAdanced) {
                          selectedDropdownValue.locationAdanced = {
                            ...selectedDropdownValue.locationAdanced,
                            limitSuburb: undefined,
                          };
                        }
                      } else {
                        setLimitSuburb({ ...limitSuburb, Label: e.value } as SuburbSuggestion);
                      }
                    }}
                    onSelect={(e) => {
                      if (selectedDropdownValue?.locationAdanced) {
                        selectedDropdownValue.locationAdanced = {
                          ...selectedDropdownValue.locationAdanced,
                          limitSuburb: e.value.Suburb,
                        };
                      }
                      setLimitSuburb(e.value);
                    }}
                  />
                </div>
              )}
            {advancedLocation != null &&
              advancedLocation > 0 &&
              advancedLocation === LOCATION_ADVANCED.SUBURB_LIMIT &&
              country !== COUNTRY.AU && (
                <>
                  <div className="w-full">
                    <div className="flex flex-col lg:w-full gap-1">
                      <div className="flex-0 w-full">
                        <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                          <div className="flex-0 w-full">
                            <label className="block text-sm font-medium">Within radius</label>
                          </div>
                        </div>
                        <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                          <div className="flex-0 w-full">
                            <REIDropDown
                              id="loc-drop-suburbradiusfrom"
                              value={
                                selectedDropdownValue?.suburbRadiusFrom ||
                                optionsdata?.LocationAdvanced?.SuburbRadius?.SuburbRadiusFrom[0].SearchRadius_From_ID
                              }
                              onSelectionChange={(e) => handleDropdownChange(e, "suburbRadiusFrom")}
                              data={optionsdata?.LocationAdvanced?.SuburbRadius?.SuburbRadiusFrom}
                              label="Label"
                              optionValue="SearchRadius_From_ID"
                              className="w-full"
                            />
                          </div>
                          <div className="flex-0 lg:w-[5%] lg:mt-3 w-full">
                            <span className="m-1">to</span>
                          </div>
                          <div className="flex-0 w-full">
                            <REIDropDown
                              id="loc-drop-suburbradiusto"
                              value={
                                selectedDropdownValue?.suburbRadiusTo ||
                                optionsdata?.LocationAdvanced?.SuburbRadius?.SuburbRadiusTo[0].SearchRadius_To_ID
                              }
                              onSelectionChange={(e) => handleDropdownChange(e, "suburbRadiusTo")}
                              data={optionsdata?.LocationAdvanced?.SuburbRadius?.SuburbRadiusTo}
                              label="Label"
                              optionValue="SearchRadius_To_ID"
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                        <div className="flex-0 w-full">
                          <label className="block text-sm font-medium">Of selected suburb</label>
                        </div>
                      </div>
                      <div className="flex-0 w-full">
                        <REIInputText
                          id="loc-txt-suburbname"
                          type="text"
                          value={suburb}
                          name="suburbname"
                          onChange={(e) => {
                            setSuburb(e.target.value);
                            if (selectedDropdownValue?.locationAdanced) {
                              selectedDropdownValue.locationAdanced = {
                                ...selectedDropdownValue.locationAdanced,
                                OflimitSuburb: e.target.value,
                              };
                            }
                          }}
                          className="w-full p-inputtext-sm"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            {advancedLocation != null &&
              advancedLocation > 0 &&
              advancedLocation === LOCATION_ADVANCED.POSTCODE_RADIUS && (
                <div className="w-full">
                  <div className="flex flex-col lg:w-full gap-1">
                    <div className="flex-0 w-full">
                      <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                        <div className="flex-0 w-full">
                          <label className="block text-sm font-medium">Postcode radius</label>
                        </div>
                      </div>
                      <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                        <div className="flex-0 w-full">
                          <REIDropDown
                            id="loc-drop-postcoderadiusfrom"
                            value={
                              selectedDropdownValue?.postcodeRadiusFrom ||
                              optionsdata?.LocationAdvanced?.PostcodeRadius?.PostcodeRadiusFrom[0]
                                .PostcodeRadius_From_ID
                            }
                            onSelectionChange={(e) => handleDropdownChange(e, "postcodeRadiusFrom")}
                            data={optionsdata?.LocationAdvanced?.PostcodeRadius?.PostcodeRadiusFrom}
                            label="Label"
                            optionValue="PostcodeRadius_From_ID"
                            className="w-full"
                          />
                        </div>
                        <div className="flex-0 w-[7%] lg:pt-2">
                          <span>to</span>
                        </div>
                        <div className="flex-0 w-full">
                          <REIDropDown
                            id="loc-drop-postcoderadiusto"
                            value={
                              selectedDropdownValue?.postcodeRadiusTo ||
                              optionsdata?.LocationAdvanced?.PostcodeRadius?.PostcodeRadiusTo[0].PostcodeRadius_To_ID
                            }
                            onSelectionChange={(e) => handleDropdownChange(e, "postcodeRadiusTo")}
                            data={optionsdata?.LocationAdvanced?.PostcodeRadius?.PostcodeRadiusTo}
                            label="Label"
                            optionValue="PostcodeRadius_To_ID"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col lg:flex-row lg:w-full gap-1">
                      <div className="flex-0 w-full">
                        <label className="block text-sm font-medium">
                          Of postcode
                          <REILinkButton
                            id="loc-lnk-postcodesearch2"
                            data-testid="loc-lnk-postcodesearch2"
                            href="http://www1.auspost.com.au/postcodes/"
                            className="no-underline"
                            target="_blank"
                            rel=""
                          >
                            <span className="ml-1">(Postcode Search)</span>
                          </REILinkButton>
                        </label>
                      </div>
                    </div>
                    <div className="flex-0 w-full">
                      <InputNumber
                        inputId="loc-num-ofpostcode"
                        data-testid="loc-num-ofpostcode"
                        value={postcode ?? undefined}
                        onBlur={(e) => {
                          setPostCode(
                            e.target.value !== undefined && e.target.value !== "" ? Number(e.target.value) : null
                          );
                          if (selectedDropdownValue?.locationAdanced) {
                            selectedDropdownValue.locationAdanced.ofPostcode = e.target.value;
                          }
                        }}
                        className={"p-inputtext-sm w-full"}
                        useGrouping={false}
                      />
                    </div>
                  </div>
                </div>
              )}
            {advancedLocation != null && advancedLocation > 0 && advancedLocation === LOCATION_ADVANCED.SUBURB_TYPE && (
              <>
                <div className="flex-0 lg:w-1/2 w-full">
                  <label className="block text-sm font-medium">Choose classification</label>
                  <div className="flex flex-wrap gap-3 mt-2 ml-1">
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="suburbmining"
                        data-testid="loc-rdo-mining"
                        name="suburbclassification"
                        value="mining"
                        onChange={(e) => setClassifications(e)}
                        checked={suburbClassification === "mining"}
                      />
                      <label htmlFor="suburbmining" className="ml-2">
                        Mining
                      </label>
                    </div>
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="suburbwaterfront"
                        data-testid="loc-rdo-waterfront"
                        name="suburbclassification"
                        value="coastal"
                        onChange={(e) => setClassifications(e)}
                        checked={suburbClassification === "coastal"}
                      />
                      <label htmlFor="suburbwaterfront" className="ml-2">
                        Waterfront
                      </label>
                    </div>
                    <div className="flex align-items-center">
                      <RadioButton
                        inputId="suburbuniversity"
                        data-testid="loc-rdo-university"
                        name="suburbclassification"
                        value="universities"
                        onChange={(e) => setClassifications(e)}
                        checked={suburbClassification === "universities"}
                      />
                      <label htmlFor="suburbuniversity" className="ml-2">
                        University
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <div className="col-span-12 text-end mt-2 text-blue-700">
        <span
          id="loc-lnk-morelocation"
          data-testid="loc-lnk-morelocation"
          className="mr-1 pt-2 cursor-pointer"
          onClick={toggleMoreLocation}
        >
          {showMoreLocation ? "Fewer" : "More"} location options
        </span>
        <i
          id="loc-icon-morelocation"
          data-testid="loc-icon-morelocation"
          className={classNames("pi cursor-pointer", showMoreLocation ? "pi-angle-up" : "pi-angle-down")}
          onClick={toggleMoreLocation}
        ></i>
      </div>
    </>
  );
};

export default LocationComponent;
